/**
 * Sanitization service for AI-generated content.
 * 
 * Security Fix RS-M-3: Uses DOMPurify with a strict allowlist to prevent
 * XSS attacks from AI responses rendered as Markdown.
 * 
 * Defense-in-depth: This runs BEFORE ngx-markdown's built-in sanitization,
 * providing an additional layer of protection against prompt injection attacks
 * that might bypass Bedrock guardrails.
 */

import { Injectable } from '@angular/core';
import DOMPurify from 'dompurify';

/**
 * Strict allowlist for Markdown-rendered content.
 * Only allows formatting tags that are safe and expected from AI responses.
 */
const ALLOWED_TAGS = [
  // Text formatting
  'p', 'br', 'hr',
  // Headings
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  // Lists
  'ul', 'ol', 'li',
  // Inline formatting
  'strong', 'b', 'em', 'i', 'u', 's', 'del', 'ins', 'mark',
  // Code
  'code', 'pre', 'kbd', 'samp',
  // Quotes and citations
  'blockquote', 'q', 'cite',
  // Tables
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
  // Other safe elements
  'span', 'div', 'a', 'img',
  // Definition lists
  'dl', 'dt', 'dd',
  // Details/summary
  'details', 'summary',
];

/**
 * Strict allowlist for attributes.
 * Prevents event handlers and dangerous attributes.
 */
const ALLOWED_ATTR = [
  // Links
  'href', 'target', 'rel',
  // Images
  'src', 'alt', 'title', 'width', 'height',
  // Tables
  'colspan', 'rowspan', 'scope',
  // General
  'class', 'id',
];

@Injectable({
  providedIn: 'root'
})
export class SanitizerService {
  constructor() {
    // Configure DOMPurify hooks for additional security
    DOMPurify.addHook('afterSanitizeAttributes', (node: Element) => {
      // Force all links to open in new tab with security attributes
      if (node.tagName === 'A') {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
      // Remove dangerous URL schemes that might have slipped through
      if (node.hasAttribute('href')) {
        const href = (node.getAttribute('href') || '').trim().toLowerCase();
        if (
          href.startsWith('javascript:') ||
          href.startsWith('data:') ||
          href.startsWith('vbscript:')
        ) {
          node.removeAttribute('href');
        }
      }
      if (node.hasAttribute('src')) {
        const src = (node.getAttribute('src') || '').trim().toLowerCase();
        if (
          src.startsWith('javascript:') ||
          src.startsWith('data:') ||
          src.startsWith('vbscript:')
        ) {
          node.removeAttribute('src');
        }
      }
    });
  }

  /**
   * Sanitize AI-generated content before Markdown rendering.
   * 
   * @param content - Raw content from AI response
   * @returns Sanitized content safe for Markdown rendering
   */
  sanitizeAIContent(content: string): string {
    if (!content) {
      return '';
    }

    return DOMPurify.sanitize(content, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ALLOW_DATA_ATTR: false,
      ALLOW_UNKNOWN_PROTOCOLS: false,
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
      RETURN_TRUSTED_TYPE: false,
    });
  }
}
